"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type DatePickerDateRange = [Date | null, Date | null]

type DateRangePickerProps = {
  dateRange: DatePickerDateRange
  setDateRange: (dateRange: DatePickerDateRange) => void
  className?: string
  align?: "start" | "center" | "end"
  presets?: boolean
  allowEmpty?: boolean
}

export function DateRangePicker({
  dateRange,
  setDateRange,
  className,
  align = "start",
  presets = true,
  allowEmpty = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [startDate, endDate] = dateRange

  const handlePresetChange = (value: string) => {
    const today = new Date()
    let newRange: DatePickerDateRange = [null, null]

    switch (value) {
      case 'today':
        newRange = [today, today]
        break
      case 'yesterday': {
        const yesterday = new Date(today.getTime() - 86400000)
        newRange = [yesterday, yesterday]
        break
      }
      case 'last7days':
        newRange = [new Date(today.getTime() - 7 * 86400000), today]
        break
      case 'last30days':
        newRange = [new Date(today.getTime() - 30 * 86400000), today]
        break
      case 'thisMonth':
        newRange = [new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0)]
        break
      case 'lastMonth':
        newRange = [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)]
        break
      case 'thisYear':
        newRange = [new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31)]
        break
    }

    setDateRange(newRange)
    setIsOpen(false)
  }

  const handleClear = () => {
    setDateRange([null, null])
    setIsOpen(false)
  }

  const handleApply = () => {
    setIsOpen(false)
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            size="sm"
            className={cn(
              "w-full justify-start text-left font-normal",
              !startDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {startDate ? (
              endDate ? (
                <>
                  {format(startDate, "LLL dd, y")} -{" "}
                  {format(endDate, "LLL dd, y")}
                </>
              ) : (
                format(startDate, "LLL dd, y")
              )
            ) : (
              <span>Select date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align}>
          {presets && (
            <div className="p-3 border-b">
              <Select
                onValueChange={handlePresetChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a preset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last7days">Last 7 days</SelectItem>
                  <SelectItem value="last30days">Last 30 days</SelectItem>
                  <SelectItem value="thisMonth">This month</SelectItem>
                  <SelectItem value="lastMonth">Last month</SelectItem>
                  <SelectItem value="thisYear">This year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <DatePicker
            selected={startDate}
            onChange={(update: DatePickerDateRange) => {
              setDateRange(update)
            }}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            monthsShown={2}
          />
          <div className="p-3 border-t flex justify-end gap-2">
            {allowEmpty && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
              >
                Clear
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
} 